/**
 * readinessAssessment.js — Score engine + derivations for the Readiness
 * experience (`/readiness`).
 *
 * Mirrors the category structure and maxima of `readinessScoring.js`
 * (Income 25 / Assets 20 / Credit 20 / Structure 15 / Docs 10 / Intent 10),
 * applied to the simplified 12–13 question assessment, and adds the
 * verification-tier model from the design handoff:
 *
 *   earned  = raw × 0.60  (self-reported information)
 *   proof   = raw × 0.25  (unlocked by uploading documents)
 *   verify  = remainder   (unlocked when we verify the proof matches)
 *
 * Displayed score = Σ earned. "Verified potential" = Σ raw.
 */
import {
  READINESS_CATEGORIES,
  numFrom,
  ruralAnswer,
  visibleQuestions,
} from '../config/readinessQuestions.js';
import { monthlyPI } from './mortgageCalculations.js';
import { recommendProducts, profileFromReadinessAnswers } from '../engine/index.js';

export const SAMPLE_CONVENTIONAL_RATE = 6.4; // illustrative only
const CONFORMING_LIMIT_BASELINE = 806500;

// ── Raw category points ─────────────────────────────────────────
/**
 * @param {Object} answers — assessment answers keyed by question id
 * @param {Object} opts
 * @param {boolean} opts.done — assessment finished (program matched)
 * @param {Object} opts.sections — saved full-application sections
 */
export function rawCategoryPoints(answers = {}, { done = false, sections = {} } = {}) {
  const a = answers;
  const pts = { income: 0, assets: 0, credit: 0, structure: 0, docs: 0, intent: 0 };

  // income (25)
  if (a.employment !== undefined) pts.income += 5;
  if (a.income !== undefined) pts.income += 5;
  const inc = numFrom(a.income);
  const debts = numFrom(a.debts);
  if (inc && a.debts !== undefined) {
    const dti = (debts * 12) / inc;
    pts.income += dti < 0.36 ? 10 : dti < 0.43 ? 7 : dti < 0.50 ? 4 : 0;
  }

  // assets (20)
  if (a.down !== undefined) {
    pts.assets += 5;
    if (a.purpose === 'purchase' && numFrom(a.value) > 0) {
      const pct = (numFrom(a.down) / numFrom(a.value)) * 100;
      pts.assets += pct >= 20 ? 12 : pct >= 10 ? 8 : pct >= 3 ? 5 : 2;
    } else if (a.purpose === 'refinance' && numFrom(a.value) > 0) {
      const equity = 1 - numFrom(a.down) / numFrom(a.value);
      pts.assets += equity >= 0.4 ? 12 : equity >= 0.2 ? 8 : equity >= 0.05 ? 5 : 2;
    }
  }

  // credit (20)
  const cmap = { '760+': 20, '700-759': 17, '660-699': 12, '600-659': 7, '<600': 3, 'unsure': 8 };
  if (a.credit !== undefined) pts.credit = cmap[a.credit] || 0;

  // structure (15)
  if (a.propertyUse !== undefined) pts.structure += 5;
  if (a.propertyType !== undefined) pts.structure += 5;
  if (done) pts.structure += 5;

  // docs (10) — nothing uploaded yet in this flow

  // intent (10)
  if (a.purpose !== undefined) pts.intent += 3;
  if (a.firstTime !== undefined || a.purpose === 'refinance') pts.intent += 2;
  if (a.military !== undefined) pts.intent += 2;
  if (a.state !== undefined) pts.intent += 1;
  if (a.county !== undefined || a.state === 'other') pts.intent += 1;

  // saved full-application sections raise the score
  const saved = (id) => sections[id] && !sections[id].skipped;
  if (saved('aboutYou')) pts.intent += 3;
  if (saved('employment')) pts.income += 5;
  if (saved('programSpecific')) pts.structure += 5;

  Object.keys(pts).forEach((k) => {
    const max = READINESS_CATEGORIES.find((c) => c.id === k).max;
    pts[k] = Math.min(pts[k], max);
  });
  return pts;
}

// ── Verification-tier breakdown ─────────────────────────────────
/**
 * Full score breakdown with verification tiers.
 * Returns { score, verifiedScore, scoreColor, scoreLabel, cats[] } where each
 * cat = { id, label, color, colorProof, colorVerif, earned, proof, verif, raw,
 *         max, ptsLabel, pct, pctProof, pctVerif, gainLabel }.
 */
export function scoreBreakdown(answers, opts) {
  const pts = rawCategoryPoints(answers, opts);
  let earnedTotal = 0;
  let rawTotal = 0;
  const cats = READINESS_CATEGORIES.map((c) => {
    const raw = pts[c.id];
    const earned = Math.round(raw * 0.6);
    const proof = Math.round(raw * 0.25);
    const verif = Math.max(raw - earned - proof, 0);
    earnedTotal += earned;
    rawTotal += raw;
    return {
      id: c.id,
      label: c.label,
      color: c.color,
      colorProof: c.color + '59',
      colorVerif: c.color + '26',
      earned,
      proof,
      verif,
      raw,
      max: c.max,
      ptsLabel: earned + '/' + c.max,
      pct: Math.round((earned / c.max) * 100) + '%',
      pctProof: Math.round((proof / c.max) * 100) + '%',
      pctVerif: Math.round((verif / c.max) * 100) + '%',
      gainLabel: raw > earned ? '+' + (raw - earned) + ' pts locked behind proof & verification' : '',
    };
  });
  const score = Math.min(earnedTotal, 100);
  const verifiedScore = Math.min(rawTotal, 100);
  return {
    score,
    verifiedScore,
    scoreColor: scoreColorFor(score),
    scoreLabel: scoreLabelFor(score),
    cats,
  };
}

export function scoreColorFor(score) {
  return score >= 80 ? '#059669' : score >= 60 ? '#4f46e5' : score >= 40 ? '#d97706' : '#71717a';
}

export function scoreLabelFor(score) {
  return score >= 80 ? 'Excellent' : score >= 60 ? 'Good' : score >= 40 ? 'Fair' : 'Building';
}

// ── Loan math ───────────────────────────────────────────────────
/** Estimated loan amount from answers (purchase: value − down; refi: balance). */
export function estimatedLoanAmount(answers = {}) {
  const value = numFrom(answers.value);
  return answers.purpose === 'purchase'
    ? Math.max(value - numFrom(answers.down), 0)
    : numFrom(answers.down);
}

export const fmtMoney = (n) => '$' + Math.round(n).toLocaleString();

// ── Key eligibility / pricing factors ───────────────────────────
/**
 * The data points that actually drive program eligibility and pricing,
 * for display in the right rail as they're collected or derived.
 * @returns Array<{ label, value, hint }>
 */
export function keyFactors(answers = {}) {
  const a = answers;
  const out = [];
  const value = numFrom(a.value);
  const loan = a.down !== undefined && value > 0 ? estimatedLoanAmount(a) : 0;

  if (loan > 0) {
    out.push({
      label: 'Est. loan amount', value: fmtMoney(loan),
      hint: loan > CONFORMING_LIMIT_BASELINE ? 'Jumbo territory' : 'Within conforming limits',
    });
    const ltv = Math.round((loan / value) * 100);
    out.push({
      label: 'Loan-to-value', value: ltv + '%',
      hint: ltv <= 80 ? 'No mortgage insurance needed' : 'Mortgage insurance applies',
    });
  }
  if (a.credit) {
    out.push({
      label: 'Credit range', value: a.credit === 'unsure' ? 'Not sure yet' : a.credit,
      hint: 'The biggest rate driver',
    });
  }
  const inc = numFrom(a.income);
  if (inc > 0 && a.debts !== undefined) {
    const dti = Math.round((numFrom(a.debts) * 12 / inc) * 100);
    out.push({
      label: 'Debt-to-income', value: dti + '%',
      hint: dti < 36 ? 'Strong' : dti < 43 ? 'Within guidelines' : 'May narrow program choice',
    });
  }
  if (a.propertyUse) {
    const useLabel = { primary: 'Primary home', secondary: 'Second home', investment: 'Investment' }[a.propertyUse];
    if (useLabel) out.push({ label: 'Occupancy', value: useLabel, hint: 'Pricing differs by use' });
  }
  return out;
}

// ── Program matching (decision engine) ──────────────────────────

/** Illustrative sample rates per program card — NOT live pricing. */
const SAMPLE_RATES = {
  'VA Loan': '6.000%', 'USDA Guaranteed': '6.100%', FHA: '6.200%',
  'Conventional 30-yr': '6.400%', 'Conventional 20-yr': '6.300%',
  'Conventional 15-yr': '6.100%', Jumbo: '6.600%', 'Bank Statement': '6.900%',
  DSCR: '7.100%', 'Specialty (Non-QM)': '7.000%',
};

const CARD_BLURBS = {
  'VA Loan': '0% down, no mortgage insurance, and competitive rates — earned through your service.',
  'USDA Guaranteed': '0% down for eligible rural and suburban areas, with low monthly mortgage insurance.',
  FHA: 'Flexible credit requirements and just 3.5% down — a strong fit for your profile.',
  Jumbo: 'For loan amounts above agency limits, with flexible terms for strong profiles.',
  'Bank Statement': 'Qualify with 12–24 months of bank statements — no tax returns needed.',
  DSCR: 'Qualify on the property\u2019s rental income instead of your personal income.',
  'Specialty (Non-QM)': 'Flexible documentation paths when standard programs don\u2019t fit.',
};

/** Program-level card name for a term-level product. */
function cardNameFor(prod) {
  if (prod.family === 'va') return 'VA Loan';
  if (prod.family === 'usda') return 'USDA Guaranteed';
  if (prod.family === 'fha') return 'FHA';
  if (prod.family === 'conventional') {
    if (prod.economics.loanLimitKind === 'jumbo') return 'Jumbo';
    const t = prod.structure.termYears;
    return `Conventional ${t && t !== 30 ? t : 30}-yr`;
  }
  if (prod.qualification.altDocKind === 'bank-statement') return 'Bank Statement';
  if (prod.qualification.dscr === 'yes') return 'DSCR';
  return 'Specialty (Non-QM)';
}

function conventionalBlurb(answers, name) {
  if (name.startsWith('Conventional')) {
    const value = numFrom(answers.value);
    const dpPct = answers.purpose === 'purchase' && value ? (numFrom(answers.down) / value) * 100 : 25;
    return dpPct >= 20
      ? 'Your 20%+ down means no PMI and the most competitive conventional pricing.'
      : 'As little as 3% down; PMI drops off automatically once you reach 20% equity.';
  }
  return CARD_BLURBS[name] || 'A strong potential fit based on the information provided.';
}

/**
 * Program cards for the results screen, derived from the decision engine's
 * ranked term-level candidates (grouped up to program level). Falls back to
 * the original heuristic when the engine has no eligible candidates yet
 * (e.g. partial answers). Shape preserved: [{ name, rate, blurb, best }].
 */
export function matchPrograms(answers = {}) {
  let candidates = [];
  try {
    const rec = recommendProducts(profileFromReadinessAnswers(answers), {
      answeredKeys: new Set(Object.keys(answers)),
    });
    candidates = rec.candidates || [];
  } catch {
    candidates = [];
  }

  const list = [];
  for (const c of candidates) {
    const name = cardNameFor(c.product);
    if (list.some((x) => x.name === name)) continue;
    list.push({
      name,
      rate: SAMPLE_RATES[name] || '6.500%',
      blurb: conventionalBlurb(answers, name),
    });
    if (list.length === 3) break;
  }

  if (!list.length) return fallbackMatchPrograms(answers);
  return list.map((p, i) => ({ ...p, best: i === 0 }));
}

/** Pre-engine heuristic, kept as the fallback for partial answer sets. */
function fallbackMatchPrograms(answers = {}) {
  const a = answers;
  const credit = a.credit || 'unsure';
  const creditNum = { '760+': 780, '700-759': 730, '660-699': 680, '600-659': 630, '<600': 580, 'unsure': 690 }[credit];
  const value = numFrom(a.value);
  const loan = estimatedLoanAmount(a);
  const dpPct = a.purpose === 'purchase' && value ? (numFrom(a.down) / value) * 100 : 25;
  const list = [];
  if (a.military === 'yes') list.push({ name: 'VA Loan', rate: SAMPLE_RATES['VA Loan'], blurb: CARD_BLURBS['VA Loan'] });
  if (ruralAnswer(a) === 'yes' && a.purpose === 'purchase') list.push({ name: 'USDA Guaranteed', rate: SAMPLE_RATES['USDA Guaranteed'], blurb: CARD_BLURBS['USDA Guaranteed'] });
  if (loan > CONFORMING_LIMIT_BASELINE) list.push({ name: 'Jumbo', rate: SAMPLE_RATES.Jumbo, blurb: CARD_BLURBS.Jumbo });
  if (creditNum >= 620 && dpPct >= 3) list.push({ name: 'Conventional 30-yr', rate: SAMPLE_RATES['Conventional 30-yr'], blurb: conventionalBlurb(a, 'Conventional 30-yr') });
  if (creditNum < 720 || dpPct < 10) list.push({ name: 'FHA', rate: SAMPLE_RATES.FHA, blurb: CARD_BLURBS.FHA });
  if (a.employment === 'self') list.push({ name: 'Bank Statement', rate: SAMPLE_RATES['Bank Statement'], blurb: CARD_BLURBS['Bank Statement'] });
  if (!list.length) list.push({ name: 'Conventional 30-yr', rate: SAMPLE_RATES['Conventional 30-yr'], blurb: 'The most common path — competitive rates with as little as 3% down.' });
  return list.slice(0, 3).map((p, i) => ({ ...p, best: i === 0 }));
}

// ── Best-fit term-level recommendation ──────────────────────────

function productDisplayName(prod) {
  // Catalog program names already carry their identity ("Freddie Mac
  // Standard Conforming", "FHA 203(b) Basic Home Mortgage", "VA IRRRL"),
  // so no agency prefix is needed.
  const term = prod.structure.termLabel || '';
  return `${prod.programName} — ${term}`.replace(/\s+/g, ' ').trim();
}

/**
 * Full decision-engine recommendation for the results screen.
 * @returns {{ state, best, alternatives, compareWithPricing, progress }|null}
 *   best = { termProgramId, displayName, reasons, cautions, confidence, fitScore }
 */
export function bestFitRecommendation(answers = {}) {
  try {
    const rec = recommendProducts(profileFromReadinessAnswers(answers), {
      answeredKeys: new Set(Object.keys(answers)),
    });
    const shape = (e) => ({
      termProgramId: e.product.termProgramId,
      displayName: productDisplayName(e.product),
      termLabel: e.product.structure.termLabel,
      family: e.product.family,
      reasons: (e.fitReasons || []).slice(0, 4),
      cautions: (e.cautions || []).slice(0, 3),
      confidence: e.confidence,
      fitScore: e.fitScore,
    });
    // Products that tie the winner on every scored criterion (the final
    // ranking between them is deterministic-but-arbitrary alphabetical) —
    // functionally interchangeable, so show them alongside the best fit.
    const equivalents = rec.best
      ? rec.candidates
        .slice(1)
        .filter((c) => c.fitScore === rec.best.fitScore
          && c.confidence === rec.best.confidence
          && c.product.family === rec.best.product.family)
        .slice(0, 3)
        .map(shape)
      : [];
    return {
      state: rec.state,
      best: rec.best ? shape(rec.best) : null,
      equivalents,
      alternatives: rec.candidates.slice(1, 4).map(shape),
      compareWithPricing: (rec.compareWithPricing || []).map(shape),
      progress: rec.progress,
      catalogVersion: rec.catalogVersion,
    };
  } catch {
    return null;
  }
}

// ── Results derivations ─────────────────────────────────────────
/**
 * Stat chips for the results hero.
 * @returns Array<{ label, value }>
 */
export function resultStats(answers, { done, verifiedScore, score }) {
  const inc = numFrom(answers.income);
  const loan = estimatedLoanAmount(answers);
  const pay = loan > 0 ? Math.round(monthlyPI(loan, SAMPLE_CONVENTIONAL_RATE, 30)) : 0;
  const dti = inc > 0 ? Math.round(((numFrom(answers.debts) + pay) / (inc / 12)) * 100) : null;
  const stats = [];
  if (loan) stats.push({ label: 'Est. loan amount', value: fmtMoney(loan) });
  if (pay) stats.push({ label: 'Est. monthly P&I', value: fmtMoney(pay) + '/mo' });
  if (dti !== null) stats.push({ label: 'Est. DTI', value: dti + '%' });
  if (done && verifiedScore > score) stats.push({ label: 'Once verified', value: 'up to ' + verifiedScore });
  return { stats, loan, pay, dti };
}

/** Improvement rows for "Fastest ways to raise your score" (max 4). */
export function improvementList(answers, { score, verifiedScore, dti }) {
  const a = answers;
  const value = numFrom(a.value);
  const improvements = [];
  if (verifiedScore > score) improvements.push({ impact: '+' + (verifiedScore - score), text: 'Upload proof (pay stubs, bank statements, tax returns) and let us verify it matches — unlocking each category\u2019s full value.' });
  if (a.credit === '600-659' || a.credit === '<600') improvements.push({ impact: '+10', text: 'Pay down card balances and keep payments on time — the highest-impact credit moves.' });
  if (a.credit === 'unsure') improvements.push({ impact: '+8', text: 'Check your actual credit score (free, no impact) so your estimate becomes real.' });
  if (dti !== null && dti > 43) improvements.push({ impact: '+6', text: 'Reducing monthly debt payments would meaningfully improve your DTI.' });
  if (a.purpose === 'purchase' && value && (numFrom(a.down) / value) < 0.2) improvements.push({ impact: '+4', text: 'Every extra point of down payment lowers your rate tier; 20% eliminates PMI.' });
  improvements.push({ impact: '+5', text: 'Complete the full application so an underwriter can pre-vet your file.' });
  return improvements.slice(0, 4);
}

export function scoreHeadlineFor(score) {
  return score >= 80 ? "You're in excellent shape"
    : score >= 60 ? "You're in a good position"
    : score >= 40 ? "You're on your way"
    : "Let's build from here";
}

export function scoreBlurbFor(score) {
  return score >= 60
    ? "Based on what you've shared, lenders will see you as a strong borrower. A few quick steps below can make your file even more competitive before anyone sees it."
    : "You have real options today, and the steps below show exactly how to strengthen your file — on your timeline, with no pressure.";
}

/** Progress across the visible question set. */
export function assessmentProgress(answers = {}) {
  const vis = visibleQuestions(answers);
  const answered = vis.filter((q) => answers[q.id] !== undefined).length;
  return { answered, total: vis.length };
}
